import { sharedCareScoutQuestions } from './careScoutAppSharedQuestions';
import { sharedCareScoutSections } from './careScoutAppSharedSections';

const questions: Record<string, PolicyFormQuestion> = {
  ...sharedCareScoutQuestions,

  // Page 22 (Acknowledgement of Nonduplication)
  ackAgentName: {
    id: 'ack-agent-name',
    questionRef: 'ackAgentName',
    title: 'Agent Name (I certify that I have done the following)',
    type: 'text',
    required: true,
  },

  informedApplicantDuplicateCoverage: {
    id: 'informed-applicant-duplicate-coverage',
    questionRef: 'informedApplicantDuplicateCoverage',
    type: 'radio',
    title:
      'Informed the undersigned applicant of the right to have all existing health insurance policies presently in force reviewed by me to determine whether duplicate coverage will occur with the issuance of this policy.',
    options: [
      {
        id: 'duplicate-will',
        value:
          'Reviewed the policies listed below and have found that duplication WILL occur with the issuance of the applied for policy',
      },
      {
        id: 'duplicate-will-not',
        value:
          'Reviewed the policies listed below and have found that duplication WILL NOT occur with the issuance of the applied for policy',
      },
    ],
  },

  policyFormNumber: {
    id: 'policy-form-number',
    questionRef: 'policyFormNumber',
    type: 'text',
    title: 'Form number',
  },

  policyCompanyInfo: {
    id: 'policy-company-info',
    questionRef: 'policyCompanyInfo',
    type: 'table',
    title: 'Company Information',
    minRows: 1,
    maxRows: 4,
    columns: [
      {
        id: 'companyName',
        title: 'Company',
        type: 'text',
      },
      {
        id: 'policyNumber',
        title: 'Policy Number',
        type: 'number',
      },
      {
        id: 'typeOfPolicy',
        title: 'Type of Policy',
        type: 'text',
      },
    ],
  },

  checkABC: {
    id: 'check-abc',
    questionRef: 'checkABC',
    title: 'Check A, B or C:',
    type: 'radio',
    format: 'vertical',
    options: [
      {
        id: 'check-a',
        value:
          'A. Duplication will not occur because the above-listed policy(ies) will be replaced by the applied-for policy',
      },
      {
        id: 'check-b',
        value: 'B. No health policies in force at this time',
      },
      {
        id: 'check-c',
        value: 'C. Applicant has elected not to have the policy(ies) reviewed',
      },
    ],
  },

  checkABCRelatedAPolicyNumber: {
    id: 'check-abc-related-a-policy-number',
    questionRef: 'checkABCRelatedAPolicyNumber',
    title: 'Policy Number',
    type: 'text',
    conditional: {
      visible: {
        field: 'checkABC',
        operator: 'equals',
        value: 'check-a',
      },
      required: {
        field: 'checkABC',
        operator: 'equals',
        value: 'check-a',
      },
    },
  },

  checkABCRelatedAFormNumber: {
    id: 'check-abc-related-a-form-number',
    questionRef: 'checkABCRelatedAFormNumber',
    title: 'Form Number',
    type: 'text',
    conditional: {
      visible: {
        field: 'checkABC',
        operator: 'equals',
        value: 'check-a',
      },
      required: {
        field: 'checkABC',
        operator: 'equals',
        value: 'check-a',
      },
    },
  },

  checkABCRelatedAJustificationForReplacement: {
    id: 'check-abc-related-a-justification-for-replacement',
    questionRef: 'checkABCRelatedAJustificationForReplacement',
    title: 'Justification for the replacement is (explain benefit to customer)',
    type: 'text',
    conditional: {
      visible: {
        field: 'checkABC',
        operator: 'equals',
        value: 'check-a',
      },
    },
  },

  agentCompanyRepresentativeName: {
    id: 'agent-company-representative-name',
    questionRef: 'agentCompanyRepresentativeName',
    title: 'Agent/Company Representative Name',
    type: 'text',
  },

  agentCompanyRepresentativeDate: {
    id: 'agent-company-representative-date',
    questionRef: 'agentCompanyRepresentativeDate',
    title: 'Date (mm/dd/yyyy)',
    type: 'date',
  },

  certifyExistingHealthPoliciesForUI: {
    id: 'certify-existing-health-policies-for-ui',
    questionRef: 'certifyExistingHealthPoliciesForUI',
    title:
      'I certify that my right to have all of my existing health policies examined has been explained to me by the agent named above.',
    type: 'radio',
    format: 'vertical',
    options: [
      {
        id: 'certify-existing-health-policies-a-for-ui',
        value:
          'I have been informed that the policy for which I am applying will result in duplicate coverage.',
      },
      {
        id: 'certify-existing-health-policies-a-related-for-ui',
        value:
          'I have been informed that the policy for which I am applying will not result in duplicate coverage.',
      },
      {
        id: 'certify-existing-health-policies-b-for-ui',
        value:
          'I have chosen to waive my right to have my policies reviewed to determine if they unnecessarily duplicate each other',
      },
    ],
  },

  certifyExistingHealthPolicies: {
    id: 'certify-existing-health-policies',
    questionRef: 'certifyExistingHealthPolicies',
    title:
      'I certify that my right to have all of my existing health policies examined has been explained to me by the agent named above.',
    type: 'radio',
    format: 'vertical',
    composite: {
      questionRefs: ['certifyExistingHealthPoliciesForUI'],
      delimiter: ' ',
      mapping: {
        'certify-existing-health-policies-a-for-ui': 'certify-existing-health-policies-a',
        'certify-existing-health-policies-a-related-for-ui': 'certify-existing-health-policies-a',
        'certify-existing-health-policies-b-for-ui': 'certify-existing-health-policies-b',
      },
    },
    hidden: true,
    disabled: true,
    options: [
      {
        id: 'certify-existing-health-policies-a',
        value:
          'I have been informed that the policy for which I am applying will result in duplicate coverage.',
      },
      {
        id: 'certify-existing-health-policies-b',
        value:
          'I have chosen to waive my right to have my policies reviewed to determine if they unnecessarily duplicate each other',
      },
    ],
  },

  certifyExistingHealthPoliciesARelated: {
    id: 'certify-existing-health-policies-a-related',
    questionRef: 'certifyExistingHealthPoliciesARelated',
    title: 'Check On',
    type: 'radio',
    composite: {
      questionRefs: ['certifyExistingHealthPoliciesForUI'],
      delimiter: ' ',
      mapping: {
        'certify-existing-health-policies-a-for-ui': 'certify-will',
        'certify-existing-health-policies-a-related-for-ui': 'certify-will-not',
      },
    },
    hidden: true,
    disabled: true,
    options: [
      {
        id: 'certify-will',
        value: 'WILL',
      },
      {
        id: 'certify-will-not',
        value: 'WILL NOT',
      },
    ],
  },
};

export const careScoutTXFormDefinition: PolicyFormDefinition = {
  sections: [
    sharedCareScoutSections.coverageSelection,
    sharedCareScoutSections.eftPaymentAuth,
    sharedCareScoutSections.applicantProfile,
    sharedCareScoutSections.insurabilityProfile,
    sharedCareScoutSections.medicalQuestions,
    sharedCareScoutSections.socialHistory,
    sharedCareScoutSections.familyHistory,
    sharedCareScoutSections.otherCoverage,
    sharedCareScoutSections.protectionLapse,
    sharedCareScoutSections.producerSignature,
    sharedCareScoutSections.ltcAppOverflowForm,
    sharedCareScoutSections.healthInsuranceAuthorization,
    sharedCareScoutSections.ltcInsurancePersonalWorksheet,
    sharedCareScoutSections.verificationFinancialNonDisclosure,
    // Page 22 (Acknowledgement of Nonduplication)
    {
      id: 'acknowledgment-of-nonduplication',
      title: 'Acknowledgement of Nonduplication',
      subsections: [
        {
          id: 'acknowledgment-of-nonduplication-questions',
          title: 'Acknowledgement of Nonduplication',
          questionGroups: [
            {
              id: 'ack-of-nonduplication-section-1',
              format: 'grid',
              questions: ['ackAgentName', 'policyFormNumber'],
            },
            {
              id: 'ack-of-nonduplication-section-2',
              format: 'default',
              questions: ['informedApplicantDuplicateCoverage', 'policyCompanyInfo'],
            },
            {
              id: 'ack-of-nonduplication-section-3',
              format: 'default',
              questions: ['checkABC'],
            },
            {
              id: 'ack-of-nonduplication-section-4',
              format: 'grid',
              questions: ['checkABCRelatedAPolicyNumber', 'checkABCRelatedAFormNumber'],
              conditional: {
                visible: {
                  field: 'checkABC',
                  operator: 'equals',
                  value: 'check-a',
                },
              },
            },
            {
              id: 'ack-of-nonduplication-section-5',
              format: 'default',
              questions: ['checkABCRelatedAJustificationForReplacement'],
              conditional: {
                visible: {
                  field: 'checkABC',
                  operator: 'equals',
                  value: 'check-a',
                },
              },
            },
            {
              id: 'ack-of-nonduplication-section-6',
              format: 'grid',
              questions: ['agentCompanyRepresentativeName', 'agentCompanyRepresentativeDate'],
            },
            {
              id: 'ack-of-nonduplication-section-7',
              format: 'default',
              questions: [
                'certifyExistingHealthPoliciesForUI',
                'certifyExistingHealthPolicies',
                'certifyExistingHealthPoliciesARelated',
              ],
            },
          ],
        },
      ],
      conditional: {
        visible: {
          operator: 'or',
          conditions: [
            {
              field: 'currentlyEnrolledMedicaid',
              operator: 'equals',
              value: 'yes',
            },
            {
              field: 'existingLtcInsurance',
              operator: 'equals',
              value: 'yes',
            },
            {
              field: 'previousLtcInsurance',
              operator: 'equals',
              value: 'yes',
            },
          ],
        },
      },
    },
    sharedCareScoutSections.ltcPotentialRateIncreaseDisclosure,
    sharedCareScoutSections.licensedInsuranceProducerInformation,
  ],
  questions,
};
